import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { RootState, AppDispatch } from './store';
import { initializeAuth } from './store/slices/authSlice';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import DashboardPage from './pages/DashboardPage';

import ConfigurationsPage from './pages/ConfigurationsPage';
import DomainsPage from './pages/DomainsPage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailsPage from './pages/TenantDetailsPage';
import TenantCreatePage from './pages/TenantCreatePage';
import TenantProvisionPage from './pages/TenantProvisionPage';
import KeyManagementPage from './pages/KeyManagementPage';
import ObjectTypesPage from './pages/ObjectTypesPage';
import DeletedTenantsPage from './pages/TenantsDeletedPage';
import ClustersPage from './pages/ClustersPage';
import ClusterCreatePage from './pages/ClusterCreatePage';
import ClusterDetailsPage from './pages/ClusterDetailsPage';
import ClustersDeletedPage from './pages/ClustersDeletedPage';

// Monitoring Pages
import MonitoringPage from './pages/monitoring/MonitoringPage';
import ContainerAppDetailsPage from './pages/monitoring/ContainerAppDetailsPage';
import AlertsPage from './pages/monitoring/AlertsPage';
import StatusDashboardPage from './pages/monitoring/StatusDashboardPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import NotificationCenter from './components/NotificationCenter';
import ModalContainer from './components/ModalContainer';
import { msalInstance } from './config/msalInstance';
import KubernetesDashboardPage from './pages/KubernetesDashboardPage';
import KubernetesClustersPage from './pages/KubernetesClustersPage';
import KubernetesWorkloadsPage from './pages/KubernetesWorkloadsPage';
import KubernetesClusterDetailsPage from './pages/KubernetesClusterDetailsPage';
import KubernetesAppDetailsPage from './pages/KubernetesAppDetailsPage';
import KubernetesLogStreamPage from './pages/KubernetesLogStreamPage';
import KubernetesServicesPage from './pages/KubernetesServicesPage';
import KubernetesDaprComponentsPage from './pages/KubernetesDaprComponentsPage';
import KubernetesExplorerPage from './pages/KubernetesExplorerPage';
import PodsExplorerPage from './pages/PodsExplorerPage';
import NamespaceDetailsPage from './pages/NamespaceDetailsPage';
import PodDetailsPage from './pages/PodDetailsPage';
import ServiceDetailsPage from './pages/ServiceDetailsPage';
import DeployImagePage from './pages/DeploymentPage';
import CacheManagementPage from './pages/CacheManagementPage';
import EnvironmentsPage from './pages/EnvironmentsPage';
import EnvironmentDetailsPage from './pages/EnvironmentDetailsPage';
import EnvironmentCreatePage from './pages/EnvironmentCreatePage';
import ServiceConfigsPage from './pages/ServiceConfigsPage';
import ServiceConfigCreatePage from './pages/ServiceConfigCreatePage';
import ServiceConfigDetailsPage from './pages/ServiceConfigDetailsPage';
import ProvisioningDefaultsPage from './pages/ProvisioningDefaultsPage';

// Initialize MSAL

const App: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
    const { darkMode } = useSelector((state: RootState) => state.ui);

    useEffect(() => {
        // Initialize authentication
        dispatch(initializeAuth());
    }, [dispatch]);

    // Apply dark mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    if (loading) {
        // Show loading spinner while initializing auth
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }
    console.log("isAuthenticated", isAuthenticated)
    return (
        <MsalProvider instance={msalInstance}>
            <Router>
                <div className={`app ${darkMode ? 'dark' : ''}`}>
                    <Routes>
                        {/* Auth routes */}
                        <Route path="/auth" element={<AuthLayout />}>
                            <Route path="login" element={<LoginPage />} />
                        </Route>

                        {/* Protected routes */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute isAuthenticated={isAuthenticated}>
                                    <MainLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<DashboardPage />} />
                            {/* Tenant routes */}
                            <Route path="tenants" element={<TenantsPage />} />
                            <Route path="tenants/create" element={<TenantCreatePage />} />
                            <Route path="tenants/provision" element={<TenantProvisionPage />} />
                            <Route path="key-management" element={<KeyManagementPage />} />
                            <Route path="object-types" element={<ObjectTypesPage />} />
                            <Route path="tenants/deleted" element={<DeletedTenantsPage />} />
                            <Route path="tenants/:id" element={<TenantDetailsPage />} />
                            <Route path="tenants/:id/edit" element={<TenantDetailsPage />} />

                            {/* Cluster routes */}
                            <Route path="clusters" element={<ClustersPage />} />
                            <Route path="clusters/create" element={<ClusterCreatePage />} />
                            <Route path="clusters/deleted" element={<ClustersDeletedPage />} />
                            <Route path="clusters/:id" element={<ClusterDetailsPage />} />
                            <Route path="clusters/:id/edit" element={<ClusterDetailsPage />} />

                            {/* Kubernetes Monitoring Routes */}
                            <Route path="kubernetes" element={<KubernetesDashboardPage />} />
                            <Route path="kubernetes/clusters" element={<KubernetesClustersPage />} />
                            <Route path="kubernetes/workloads" element={<KubernetesWorkloadsPage />} />
                            <Route path="kubernetes/services" element={<KubernetesServicesPage />} />
                            <Route path="kubernetes/dapr-components" element={<KubernetesDaprComponentsPage />} />
                            <Route path="kubernetes/clusters/:clusterId" element={<KubernetesClusterDetailsPage />} />
                            <Route path="kubernetes/apps/:clusterId/:namespace/:name" element={<KubernetesAppDetailsPage />} />
                            <Route path="kubernetes/logs" element={<KubernetesLogStreamPage />} />
                            <Route path="kubernetes/explorer" element={<KubernetesExplorerPage />} />
                            <Route path="kubernetes/pods" element={<PodsExplorerPage />} />
                            <Route path="kubernetes/clusters/:clusterName/details" element={<ClusterDetailsPage />} />
                            <Route path="kubernetes/namespaces/:clusterName/:namespace" element={<NamespaceDetailsPage />} />
                            <Route path="kubernetes/services/:clusterName/:namespace/:serviceName" element={<ServiceDetailsPage />} />

                            <Route path="deployment" element={<DeployImagePage />} />
                            <Route path="environments" element={<EnvironmentsPage />} />
                            <Route path="environments/create" element={<EnvironmentCreatePage />} />
                            <Route path="environments/:id" element={<EnvironmentDetailsPage />} />

                            {/* Monitoring routes - New */}
                            <Route path="monitoring" element={<MonitoringPage />} />
                            <Route path="monitoring/apps/:resourceGroup/:appName" element={<ContainerAppDetailsPage />} />
                            <Route path="monitoring/alerts" element={<AlertsPage />} />
                            <Route path="monitoring/status" element={<StatusDashboardPage />} />

                            <Route path="cache-management" element={<CacheManagementPage />} />
                            {/* Service Config routes */}
                            <Route path="service-configs" element={<ServiceConfigsPage />} />
                            <Route path="service-configs/create" element={<ServiceConfigCreatePage />} />
                            <Route path="service-configs/:id" element={<ServiceConfigDetailsPage />} />
                            <Route path="service-configs/:id/edit" element={<ServiceConfigDetailsPage />} />
                            <Route path="configurations" element={<ConfigurationsPage />} />
                            <Route path="domains" element={<DomainsPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="provisioning-defaults" element={<ProvisioningDefaultsPage />} />
                        </Route>

                        {/* 404 page */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>

                    {/* Global components */}
                    <NotificationCenter />
                    <ModalContainer />
                </div>
            </Router>
        </MsalProvider>
    );
};

export default App;